import { useParams, Link, Navigate } from "react-router-dom";
import { Check, ArrowRight, Phone, MapPin, DollarSign } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import SEO from "@/components/site/SEO";
import QuoteForm from "@/components/site/QuoteForm";
import { SERVICES, SERVICE_CONTENT, SORTED_AREAS, COMPANY } from "@/lib/constants";

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.slug === slug);
  if (!service) return <Navigate to="/services" replace />;

  const content = SERVICE_CONTENT[slug] || {};
  const faqs = content.faqs?.length ? content.faqs : service.faqs;
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const nearbyAreas = SORTED_AREAS.slice(0, 12);

  const schema = [
    {
      "@context": "https://schema.org",
      "@type": "Service",
      serviceType: service.title,
      provider: {
        "@type": "GeneralContractor",
        name: COMPANY.name,
        telephone: COMPANY.phone,
        address: { "@type": "PostalAddress", addressLocality: COMPANY.city, addressRegion: COMPANY.state, postalCode: COMPANY.zip },
      },
      areaServed: SORTED_AREAS.map((a) => `${a.city}, ${a.state}`),
      description: service.description,
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: origin + "/" },
        { "@type": "ListItem", position: 2, name: "Services", item: origin + "/services" },
        { "@type": "ListItem", position: 3, name: service.title, item: origin + `/services/${slug}` },
      ],
    },
  ];

  return (
    <div>
      <SEO
        title={content.metaTitle || `${service.title} in Watertown, WI | CG Concrete LLC`}
        description={content.metaDescription || `${service.short} Serving Watertown, WI & nearby. Free estimates — call ${COMPANY.phone}.`}
        schema={schema}
      />

      <section className="relative bg-secondary text-white">
        <img src={service.image} alt={`${service.title} by CG Concrete in Watertown, WI`} className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-secondary/60 grain-texture" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
          <div className="flex items-center gap-2 text-sm text-white/60 mb-4">
            <Link to="/services" className="hover:text-primary">Services</Link> <span>/</span> <span className="text-white">{service.title}</span>
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold max-w-3xl">{service.title} in {COMPANY.city}, {COMPANY.state}</h1>
          <p className="text-white/80 mt-5 text-lg max-w-2xl">{service.short}</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Button asChild className="bg-primary hover:bg-primary/90 rounded-sm font-bold h-12 px-6" data-testid="service-hero-quote">
              <Link to={`/contact?service=${encodeURIComponent(service.title)}`}>Get a Free Estimate <ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
            <Button asChild variant="outline" className="rounded-sm font-bold h-12 px-6 bg-transparent border-white/40 text-white hover:bg-white hover:text-secondary">
              <a href={`tel:${COMPANY.phoneRaw}`}><Phone className="mr-2 h-4 w-4" /> {COMPANY.phone}</a>
            </Button>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 grid lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2 space-y-14">
          {/* Answer-first block for AEO */}
          <div className="bg-primary/5 border-l-4 border-primary p-6">
            <p className="text-lg leading-relaxed">{service.description}</p>
          </div>

          {/* Overview long-form */}
          {content.overview && (
            <div className="space-y-4">
              {content.overview.map((p, i) => (
                <p key={i} className="text-muted-foreground leading-relaxed text-lg">{p}</p>
              ))}
            </div>
          )}

          <div>
            <h2 className="font-heading text-2xl font-bold mb-5">What's Included</h2>
            <ul className="grid sm:grid-cols-2 gap-3">
              {service.features.map((f) => (
                <li key={f} className="flex items-start gap-3 bg-card border border-border p-4"><Check className="h-5 w-5 text-primary mt-0.5 shrink-0" /><span className="font-medium">{f}</span></li>
              ))}
            </ul>
          </div>

          {/* Process */}
          {content.process && (
            <div>
              <h2 className="font-heading text-2xl font-bold mb-6">How We Do It</h2>
              <ol className="space-y-5" data-testid="service-process">
                {content.process.map((step, i) => (
                  <li key={i} className="flex gap-4">
                    <span className="h-9 w-9 shrink-0 bg-primary text-white font-heading font-extrabold flex items-center justify-center">{i + 1}</span>
                    <div>
                      <h3 className="font-bold text-lg leading-tight">{step.title}</h3>
                      <p className="text-muted-foreground mt-1 leading-relaxed">{step.text}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {/* Wisconsin-specific */}
          {content.local && (
            <div className="bg-secondary text-white grain-texture p-8">
              <h2 className="font-heading text-2xl font-bold text-primary">{content.local.heading}</h2>
              <div className="mt-4 space-y-3">
                {content.local.body.map((p, i) => (
                  <p key={i} className="text-white/85 leading-relaxed">{p}</p>
                ))}
              </div>
            </div>
          )}

          {/* Cost factors */}
          {content.costFactors && (
            <div>
              <h2 className="font-heading text-2xl font-bold mb-2">What Affects Your Project Cost</h2>
              <p className="text-muted-foreground mb-5">Every job is quoted individually. Here's what we look at when pricing your {service.title.toLowerCase()}:</p>
              <ul className="grid sm:grid-cols-2 gap-3" data-testid="service-cost-factors">
                {content.costFactors.map((c) => (
                  <li key={c} className="flex items-start gap-3 bg-card border border-border p-4"><DollarSign className="h-5 w-5 text-primary mt-0.5 shrink-0" /><span className="text-sm font-medium">{c}</span></li>
                ))}
              </ul>
            </div>
          )}

          {/* FAQs */}
          <div>
            <h2 className="font-heading text-2xl font-bold mb-5">Frequently Asked Questions</h2>
            <Accordion type="single" collapsible className="border border-border divide-y divide-border" data-testid="service-faqs">
              {faqs.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="px-5">
                  <AccordionTrigger className="font-bold text-left" data-testid={`faq-${i}`}>{f.q}</AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>

          {/* Local service areas */}
          <div>
            <h2 className="font-heading text-2xl font-bold mb-2">{service.title} Near You</h2>
            <p className="text-muted-foreground mb-5">We pour {service.title.toLowerCase()} throughout {COMPANY.city} and the surrounding communities of Jefferson, Dodge, and Waukesha counties.</p>
            <div className="flex flex-wrap gap-2">
              {nearbyAreas.map((a) => (
                <Link key={a.slug} to={`/service-areas/${a.slug}`} className="flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 border border-border hover:border-primary hover:text-primary transition-colors" data-testid={`service-area-${a.slug}`}>
                  <MapPin className="h-3.5 w-3.5 text-primary" /> {a.city}
                </Link>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap gap-2 pt-2 border-t border-border">
            <span className="text-sm text-muted-foreground mr-2 self-center pt-4">Other services:</span>
            {SERVICES.filter((s) => s.slug !== slug).map((s) => (
              <Link key={s.slug} to={`/services/${s.slug}`} className="text-sm font-semibold px-3 py-1.5 mt-4 border border-border hover:border-primary hover:text-primary transition-colors">{s.title}</Link>
            ))}
          </div>
        </div>

        <div className="lg:col-span-1">
          <div className="sticky top-24">
            <div className="bg-secondary text-white p-6 mb-4">
              <h3 className="font-heading text-xl font-bold">Get a Free Estimate</h3>
              <p className="text-white/70 text-sm mt-1">Prefer to talk? Call us directly.</p>
              <Button asChild variant="outline" className="mt-4 w-full bg-transparent border-white/40 text-white hover:bg-white hover:text-secondary rounded-sm font-bold">
                <a href={`tel:${COMPANY.phoneRaw}`}><Phone className="mr-2 h-4 w-4" /> {COMPANY.phone}</a>
              </Button>
            </div>
            <QuoteForm defaultService={service.title} />
          </div>
        </div>
      </div>
    </div>
  );
}
